import { useRef, useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { PageHeader, logoSrc } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Clock, MessageSquare, Mail, Undo2, Users, CalendarDays, ChevronRight } from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";
import SettingsIssuesDialog from "@/components/SettingsIssuesDialog";
import CalendarSyncCard from "@/components/CalendarSyncCard";
import { format, parseISO } from "date-fns";

export default function Settings() {
  const { settings, refresh } = useSettings();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dayStart, setDayStart] = useState("08:30");
  const [dayEnd, setDayEnd] = useState("17:00");
  const [savingHours, setSavingHours] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [businessAbn, setBusinessAbn] = useState("");
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [imports, setImports] = useState([]);
  const logo = logoSrc(settings);
  const { alerts = {}, refreshAlerts } = useOutletContext() || {};
  const phoneRef = useRef(null);
  const phoneMissing = !businessPhone.trim();
  const focusPhone = () => {
    phoneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => phoneRef.current?.focus(), 350);
  };

  const loadImports = () => api.get("/imports").then((r) => setImports(r.data)).catch(() => {});
  useEffect(() => { loadImports(); }, []);

  useEffect(() => {
    if (settings?.day_start) setDayStart(settings.day_start);
    if (settings?.day_end) setDayEnd(settings.day_end);
    if (settings?.business_name != null) setBusinessName(settings.business_name);
    if (settings?.business_phone != null) setBusinessPhone(settings.business_phone);
    if (settings?.business_abn != null) setBusinessAbn(settings.business_abn);
    if (settings?.email_enabled != null) setEmailEnabled(settings.email_enabled);
    if (settings?.email_subject != null) setEmailSubject(settings.email_subject);
    if (settings?.email_body != null) setEmailBody(settings.email_body);
  }, [settings]);

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await api.put("/settings/email", {
        email_enabled: emailEnabled,
        email_subject: emailSubject,
        email_body: emailBody,
      });
      toast.success("Email reminder settings saved");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingEmail(false);
    }
  };

  const undoImport = async (batch) => {
    const label = batch.kind === "customers" ? "clients" : "bookings";
    if (!window.confirm(`Undo this import? This will permanently delete the ${batch.count} ${label} added in this batch.`)) return;
    try {
      const { data } = await api.delete(`/imports/${batch.batch_id}`);
      toast.success(`Removed ${data.deleted} ${label}`);
      loadImports();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveBusiness = async () => {
    if (!businessName.trim()) return toast.error("Business name is required");
    setSavingBusiness(true);
    try {
      await api.put("/settings/business", { business_name: businessName.trim(), business_phone: businessPhone.trim(), business_abn: businessAbn.trim() });
      toast.success("Business details updated");
      refresh();
      refreshAlerts?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingBusiness(false);
    }
  };

  const saveHours = async () => {
    setSavingHours(true);
    try {
      await api.put("/settings/hours", { day_start: dayStart, day_end: dayEnd });
      toast.success("Working hours updated");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingHours(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.put("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Brand & Preferences"
        title="Settings"
        description="Manage the branding shown across your bookings manager."
      />

      <SettingsIssuesDialog alerts={alerts} onFixPhone={focusPhone} />
      <div className="max-w-xl">
        <div className="label-caps text-muted-foreground mb-4">Business</div>
        <div className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm mb-12">
          <div className="space-y-2 max-w-sm">
            <Label className="label-caps text-muted-foreground">Business name</Label>
            <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="business-name-input" placeholder="The Sterling Touch" />
          </div>
          <div className="space-y-2 max-w-sm mt-6">
            <Label className="label-caps text-muted-foreground">Business phone</Label>
            <Input ref={phoneRef} value={businessPhone} onChange={(e) => setBusinessPhone(e.target.value)} data-testid="business-phone-input" placeholder="e.g. 0412 345 678"
              className={phoneMissing ? "border-destructive/60 focus-visible:ring-destructive/40" : ""} />
            {phoneMissing && <p className="text-xs text-destructive" data-testid="business-phone-missing">Required — add a phone number, then click Save Business Details.</p>}
          </div>
          <div className="space-y-2 max-w-sm mt-6">
            <Label className="label-caps text-muted-foreground">Business ABN</Label>
            <Input value={businessAbn} onChange={(e) => setBusinessAbn(e.target.value)} placeholder="11 digits" inputMode="numeric" data-testid="business-abn-input" />
            <p className="text-xs text-muted-foreground">Printed on client tax statements (BAS / income tax).</p>
          </div>
          <Button onClick={saveBusiness} disabled={savingBusiness} data-testid="save-business-button" className="rounded-sm mt-6">
            {savingBusiness ? "Saving…" : "Save Business Details"}
          </Button>
          <p className="text-sm text-muted-foreground mt-3">
            The name shows in the top bar. The phone fills <code>{"{business_phone}"}</code> in client texts so they know who to call.
          </p>
        </div>

        <div className="label-caps text-muted-foreground mb-4">Company logo</div>
        <div className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm">
          <div className="flex items-center justify-center bg-background border border-dashed border-border/70 rounded-sm h-40 mb-6">
            {logo ? (
              <img src={logo} alt="Logo preview" data-testid="settings-logo-preview" className="max-h-32 object-contain" />
            ) : (
              <span className="text-muted-foreground text-sm">No logo uploaded</span>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="logo-file-input" />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="upload-logo-button"
            className="rounded-sm gap-2"
          >
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload New Logo"}
          </Button>
          <p className="text-sm text-muted-foreground mt-3">
            PNG or JPG recommended. Shown in the sidebar beneath your business name.
          </p>
        </div>

        <div className="label-caps text-muted-foreground mb-4 mt-12">Working hours</div>
        <div className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm">
          <p className="text-sm text-muted-foreground mb-6 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Sets the start and end times shown on the Day and Week schedule grids.
          </p>
          <div className="grid grid-cols-2 gap-6 max-w-sm">
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Day starts</Label>
              <Input type="time" value={dayStart} onChange={(e) => setDayStart(e.target.value)} data-testid="hours-start" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Day ends</Label>
              <Input type="time" value={dayEnd} onChange={(e) => setDayEnd(e.target.value)} data-testid="hours-end" />
            </div>
          </div>
          <Button onClick={saveHours} disabled={savingHours} data-testid="save-hours-button" className="rounded-sm mt-6">
            {savingHours ? "Saving…" : "Save Working Hours"}
          </Button>
          <p className="text-sm text-muted-foreground mt-3">
            Bookings outside these hours will still show — the grid expands to include them.
          </p>
        </div>

        <div className="label-caps text-muted-foreground mb-4 mt-12">Calendar sync</div>
        <CalendarSyncCard />

        <div className="label-caps text-muted-foreground mb-4 mt-12">SMS notifications</div>
        <Link to="/settings/sms" data-testid="open-sms-notifications" className="group flex items-center justify-between border border-border/60 bg-card p-4 sm:p-8 rounded-sm hover:border-brand transition-colors">
          <div>
            <div className="flex items-center gap-2 font-medium">
              <MessageSquare className="w-4 h-4 text-brand" /> SMS Notifications
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Twilio reminders, template, timing, test SMS and delivery history.
            </p>
            <div className="mt-3 text-xs" data-testid="sms-mode-summary">
              {settings?.sms_live ? "Live sending is ON" : "Test mode — no live SMS are sent"}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-brand group-hover:translate-x-1 transition-transform" />
        </Link>

        <div className="label-caps text-muted-foreground mb-4 mt-12">Email reminders</div>
        <div className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm">
          {settings?.email_configured ? (
            <div className="mb-6 inline-flex items-center gap-2 border border-brand/50 bg-brand/10 rounded-sm px-3 py-1.5 text-sm" data-testid="email-connected">
              <span className="w-2 h-2 rounded-full bg-[hsl(140_60%_45%)]" /> Resend connected — sending from {settings?.sender_email}
            </div>
          ) : (
            <div className="mb-6 border border-brand/60 bg-brand/10 rounded-sm px-4 py-3 text-sm" data-testid="email-not-configured">
              Email is not connected yet. Add your Resend API key to start sending. Reminders stay off until then.
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 font-medium">
                <Mail className="w-4 h-4" /> Send reminders automatically
              </div>
              <p className="text-sm text-muted-foreground mt-1">Email each client before their booking (uses the same reminder timing as SMS).</p>
            </div>
            <Switch checked={emailEnabled} onCheckedChange={setEmailEnabled} data-testid="email-enabled-switch" />
          </div>

          <div className="space-y-2 mt-6">
            <Label className="label-caps text-muted-foreground">Subject line</Label>
            <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} data-testid="email-subject-input" />
          </div>

          <div className="space-y-2 mt-6">
            <Label className="label-caps text-muted-foreground">Email message</Label>
            <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={6} data-testid="email-body-input" />
            <p className="text-xs text-muted-foreground">
              Placeholders: <code>{"{client}"}</code>, <code>{"{date}"}</code>, <code>{"{time}"}</code>, <code>{"{duration}"}</code>, <code>{"{price}"}</code>, <code>{"{business}"}</code>. <code>{"{price}"}</code> includes 10% GST.
            </p>
          </div>

          <Button onClick={saveEmail} disabled={savingEmail} data-testid="save-email-button" className="rounded-sm mt-6">
            {savingEmail ? "Saving…" : "Save Email Settings"}
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            Each client's <strong>Reminder channel</strong> (set on their profile) decides whether they get SMS, email, or both.
          </p>
        </div>

        <div className="label-caps text-muted-foreground mb-4 mt-12">Recent imports</div>
        <div className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm" data-testid="imports-panel">
          {imports.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No imports yet. When you import clients or bookings from CSV, each batch appears here so you can undo it if a file was wrong.
            </p>
          ) : (
            <div className="divide-y divide-border/60">
              {imports.map((b) => (
                <div key={b.batch_id} className="flex items-center justify-between py-4" data-testid={`import-batch-${b.batch_id}`}>
                  <div className="flex items-center gap-3">
                    {b.kind === "customers" ? <Users className="w-4 h-4 text-brand" /> : <CalendarDays className="w-4 h-4 text-brand" />}
                    <div>
                      <div className="font-medium text-sm">
                        {b.count} {b.kind === "customers" ? "clients" : "bookings"}
                        {b.filename ? <span className="text-muted-foreground font-normal"> · {b.filename}</span> : null}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {b.created_at ? format(parseISO(b.created_at), "d MMM yyyy, h:mma") : ""} · {b.created_by}
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => undoImport(b)} data-testid={`undo-import-${b.batch_id}`} className="rounded-sm gap-2 text-destructive hover:text-destructive">
                    <Undo2 className="w-4 h-4" /> Undo
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
